"use client";

import * as React from "react";
import type { CheckoutOpeningMode } from "~/lib/checkoutOpeningMode";

type CheckoutOpeningControlProps = {
  mode: CheckoutOpeningMode;
  offerKey: string | null;
  buyable: boolean;
  billingNote: string;
  unavailableMessage: string;
  source: string;
  onBuy: () => void;
  buttonLabel?: string;
};

type WaitlistFormState =
  | { kind: "ready" }
  | { kind: "submitting" }
  | { kind: "success"; message: string }
  | { kind: "error"; message: string };

export const CHECKOUT_WAITLIST_CONFIRMATION =
  "You're on the waitlist. We'll be in touch when a place opens.";

export function CheckoutWaitlistForm({
  offerKey,
  source,
}: Pick<CheckoutOpeningControlProps, "offerKey" | "source">) {
  const [email, setEmail] = React.useState("");
  const [name, setName] = React.useState("");
  const [formState, setFormState] = React.useState<WaitlistFormState>({
    kind: "ready",
  });

  if (formState.kind === "success") {
    return (
      <div
        className="checkout-waitlist-confirmation flex min-h-[64px] items-center rounded-lg border border-[#788c5d]/30 bg-[#e8efd9] px-3.5 py-3 text-left text-[13px] leading-[1.45] text-[#475a32]"
        role="status"
      >
        {formState.message}
      </div>
    );
  }

  async function submitWaitlist(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (formState.kind === "submitting") return;
    setFormState({ kind: "submitting" });
    try {
      const response = await fetch("/api/checkout/waitlist", {
        method: "POST",
        credentials: "same-origin",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email,
          name: name || null,
          offerKey,
          source,
        }),
      });
      const payload = (await response.json().catch(() => null)) as {
        message?: unknown;
      } | null;
      if (!response.ok) {
        throw new Error(
          typeof payload?.message === "string"
            ? payload.message
            : "The waitlist form could not be submitted. Please try again."
        );
      }
      setFormState({
        kind: "success",
        message:
          typeof payload?.message === "string"
            ? payload.message
            : CHECKOUT_WAITLIST_CONFIRMATION,
      });
    } catch (error) {
      setFormState({
        kind: "error",
        message:
          error instanceof Error
            ? error.message
            : "The waitlist form could not be submitted. Please try again.",
      });
    }
  }

  return (
    <form
      className="checkout-waitlist-form grid w-full gap-3 text-left"
      id="waitlist"
      onSubmit={(event) => void submitWaitlist(event)}
    >
      <label
        className="grid gap-1.5 text-[12px] font-semibold text-[#2a2a26]"
        htmlFor="checkout-waitlist-email"
      >
        Email
        <input
          className="min-h-[44px] w-full rounded-lg border border-[#d8d5c4] bg-white px-3 text-[14px] font-normal text-[#141413] outline-none focus:border-[#d97757] focus:ring-2 focus:ring-[#d97757]/20 disabled:bg-[#e8e6dc]"
          id="checkout-waitlist-email"
          name="email"
          type="email"
          autoComplete="email"
          required
          maxLength={254}
          value={email}
          disabled={formState.kind === "submitting"}
          onChange={(event) => setEmail(event.target.value)}
        />
      </label>
      <label
        className="grid gap-1.5 text-[12px] font-semibold text-[#2a2a26]"
        htmlFor="checkout-waitlist-name"
      >
        <span className="flex items-baseline gap-1.5">
          Name
          <small className="text-[11px] font-normal text-[#6a6a62]">
            Optional
          </small>
        </span>
        <input
          className="min-h-[44px] w-full rounded-lg border border-[#d8d5c4] bg-white px-3 text-[14px] font-normal text-[#141413] outline-none focus:border-[#d97757] focus:ring-2 focus:ring-[#d97757]/20 disabled:bg-[#e8e6dc]"
          id="checkout-waitlist-name"
          name="name"
          type="text"
          autoComplete="name"
          maxLength={160}
          value={name}
          disabled={formState.kind === "submitting"}
          onChange={(event) => setName(event.target.value)}
        />
      </label>
      {formState.kind === "error" ? (
        <p
          className="checkout-waitlist-error text-[12px] leading-[1.45] text-[#8a2a18]"
          role="alert"
        >
          {formState.message}
        </p>
      ) : null}
      <button
        type="submit"
        className="access-button inline-flex min-h-[44px] w-full items-center justify-center rounded-lg border border-[#141413] bg-[#141413] px-4 text-[13.5px] font-medium text-[#faf9f5] transition hover:bg-[#2a2a26] disabled:cursor-not-allowed"
        disabled={formState.kind === "submitting"}
      >
        {formState.kind === "submitting" ? "Joining…" : "Join the waitlist"}
      </button>
    </form>
  );
}

export default function CheckoutOpeningControl({
  mode,
  offerKey,
  buyable,
  billingNote,
  unavailableMessage,
  source,
  onBuy,
  buttonLabel = "Subscribe",
}: CheckoutOpeningControlProps) {
  if (mode === "buyable") {
    return (
      <>
        <button
          type="button"
          className="access-button"
          disabled={!buyable}
          onClick={onBuy}
        >
          {buttonLabel} <span className="arrow">→</span>
        </button>
        {(buyable ? billingNote : unavailableMessage) ? (
          <span className="plan-fine">
            {buyable ? billingNote : unavailableMessage}
          </span>
        ) : null}
      </>
    );
  }

  if (mode === "sold_out") {
    return (
      <div className="checkout-opening-status" role="status">
        <strong>Sold out</strong>
        <span>Premium is currently sold out.</span>
      </div>
    );
  }

  return <CheckoutWaitlistForm offerKey={offerKey} source={source} />;
}
